import type { TicketAlertClockConfig, TicketStageResponse } from '@troia-v3/types';
import { SYSTEM_TICKET_ALERT_DEFAULTS } from '@troia-v3/types';

/**
 * Defaults do sistema com fallback local. Blindagem contra o dev server do Vite
 * servir um pré-bundle antigo de `@troia-v3/types` onde `SYSTEM_TICKET_ALERT_DEFAULTS`
 * ainda não existe (const `undefined` em runtime, `tsc` verde) — issue conhecida ao
 * bumpar o types com o dev server vivo. Mesma blindagem de
 * `funnels/components/FunnelAlertsTab.tsx:16-20`. Também serve de floor do produto.
 *
 * 24h/72h = 1 dia para o aviso, 3 dias para o crítico. Escala de chamado, não de
 * lead: o default de lead é 14/30 DIAS (`@troia-v3/types` alerts.ts:50-51) porque
 * mede negociação; aqui mede fila de atendimento.
 */
export const TICKET_ALERT_DEFAULTS: { timeInStage: TicketAlertClockConfig } =
  SYSTEM_TICKET_ALERT_DEFAULTS ?? {
    timeInStage: { enabled: true, warningHours: 24, criticalHours: 72 },
  };

export interface ClockFormState {
  enabled: boolean;
  warningHours: number;
  criticalHours: number;
}

export interface StageClockDraft {
  warningHours: number;
  criticalHours: number;
}

export const clockToForm = (
  clock: TicketAlertClockConfig | undefined,
  fallback: TicketAlertClockConfig,
): ClockFormState => ({
  enabled: clock?.enabled ?? fallback.enabled,
  warningHours: clock?.warningHours ?? fallback.warningHours,
  criticalHours: clock?.criticalHours ?? fallback.criticalHours,
});

/**
 * `criticalHours > warningHours >= 1` — ESPELHO EXATO do `.refine()` do
 * `ticketAlertConfigSchema` (backend, tarefa C3). Divergir aqui produz o pior
 * modo de falha de formulário: o botão salva, o servidor rejeita com 422, e a
 * mensagem que o usuário lê não é a que o campo violou.
 */
export const validateClockHours = (warningHours: number, criticalHours: number): string | null => {
  if (!Number.isFinite(warningHours) || warningHours < 1) {
    return 'O alerta deve ser de pelo menos 1 hora.';
  }
  if (!Number.isFinite(criticalHours) || criticalHours <= warningHours) {
    return 'O crítico deve ser maior que o alerta.';
  }
  return null;
};

export const validateClock = (clock: ClockFormState): string | null => {
  if (!clock.enabled) return null;
  return validateClockHours(clock.warningHours, clock.criticalHours);
};

export const buildSummaryText = (timeInStage: ClockFormState): string => {
  if (!timeInStage.enabled) {
    return 'Relógio desligado — os cards não mostrarão o trilho de tempo na etapa.';
  }
  return `Tempo na etapa alerta em ${timeInStage.warningHours}h, crítico em ${timeInStage.criticalHours}h.`;
};

export const hasStageOverride = (stage: TicketStageResponse): boolean =>
  !!(stage.alertConfig && stage.alertConfig.timeInStage);
